import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { DECOMP, GRADE_EVIDENCE, OBS, QTR_SEL, REAL, SECTOR_SEL } from "./allocatorData";

const TT = { background: "#0b0b0f", border: "1px solid #27272a", fontSize: 11 };

export default function AllocSkill() {
  const g = REAL.grades.MFEGX;
  const tot = DECOMP.reduce((s, d) => s + d.v, 0);
  const style = DECOMP.filter((d) => d.k !== "Stock selection").reduce((s, d) => s + d.v, 0);
  const pos = QTR_SEL.filter((x) => x > 0).length;
  return (
    <div className="space-y-4" data-testid="persona-alloc-skill">
      <PainBanner testid="persona-alloc-skill-pain" persona="LONG-ONLY ALLOCATOR · SKILL OR STYLE"
        pain="Is this manager's outperformance real stock-picking, or a growth tilt I could buy for 5 bp?"
        answer="excess return split into factor tilts vs selection, plus forward positioning" />
      <div>
        <Headline testid="alloc-skill-headline">MFS Growth (MFEGX): {Math.round((style / tot) * 100)}% of the excess came from style tilts — but its positioning today grades {g.letter}</Headline>
        <div className="text-sm text-muted-foreground mt-1">Holdings-based attribution from N-PORT (long book) · 3 years · vs Russell 1000 Growth proxy</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="alloc-skill-kpi-excess" label="Excess return / yr" value={`+${tot.toFixed(1)}%`} sub="3y, vs benchmark (sample)" />
        <Kpi testid="alloc-skill-kpi-selection" label="From stock selection" value="+0.6%" tone="text-emerald-400" sub={`positive in ${pos} of ${QTR_SEL.length} quarters`} />
        <Kpi testid="alloc-skill-kpi-grade" label="Positioning grade (real)" value={g.letter} tone="text-emerald-400" sub={`peer z +${g.grade.toFixed(2)} · top quintile · ${OBS}`} />
        <Kpi testid="alloc-skill-kpi-leader" label="Leader vs follower (real)" value={`${Math.round(g.lead_exp * 100)}% / ${Math.round(g.follow_exp * 100)}%`} sub="held before specialists converged / added after" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2"><CardContent className="pt-4">
          <SectionTitle>Where the excess return came from (% / yr)</SectionTitle>
          <div className="h-[260px]" data-testid="alloc-skill-decomp-chart">
            <ResponsiveContainer>
              <BarChart data={DECOMP} layout="vertical" margin={{ top: 5, right: 40, left: 40, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="k" width={150} tick={{ fontSize: 11, fill: "#d4d4d8" }} />
                <ReferenceLine x={0} stroke="#52525b" />
                <Tooltip contentStyle={TT} formatter={(v) => [`${v > 0 ? "+" : ""}${v}% / yr`, "contribution"]} />
                <Bar dataKey="v" radius={[0, 3, 3, 0]} label={{ position: "right", fontSize: 10, fill: "#a1a1aa", formatter: (v) => `${v > 0 ? "+" : ""}${v}%` }}>
                  {DECOMP.map((d) => <Cell key={d.k} fill={d.k === "Stock selection" ? "#34d399" : d.v < 0 ? "#f87171" : "#71717a"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted-foreground">Grey = return you could replicate with cheap factor exposure (red = a factor tilt that cost return). Green = what the manager added by choosing stocks.</div>
          <div className="mt-4" data-testid="alloc-skill-quarters">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Selection effect by quarter (consistency)</div>
            <div className="flex gap-1 h-20">
              {QTR_SEL.map((x, i) => (
                <div key={i} className="flex-1 flex flex-col h-full" title={`${x}%`}>
                  <div className="flex-1 flex items-end">{x > 0 && <div className="w-full rounded-t-sm bg-emerald-500/70" style={{ height: `${x * 100}%` }} />}</div>
                  <div className="h-px bg-zinc-500" />
                  <div className="flex-1 flex items-start">{x < 0 && <div className="w-full rounded-b-sm bg-red-500/60" style={{ height: `${-x * 100}%` }} />}</div>
                </div>
              ))}
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <SectionTitle>Selection hit rate by sector</SectionTitle>
          <div className="space-y-2" data-testid="alloc-skill-sectors">
            {SECTOR_SEL.map((s) => (
              <div key={s.s} className="text-xs">
                <div className="flex justify-between"><span>{s.s} <span className="text-muted-foreground">· {s.w}% of book</span></span>
                  <span className={`font-mono ${s.hit >= 55 ? "text-emerald-400" : s.hit < 50 ? "text-red-400" : ""}`}>{s.hit}%</span></div>
                <div className="h-1.5 rounded-full bg-zinc-800 mt-1 overflow-hidden"><div className={`h-full ${s.hit >= 55 ? "bg-emerald-500" : s.hit < 50 ? "bg-red-500" : "bg-zinc-500"}`} style={{ width: `${s.hit}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded border border-border/60 p-3 text-[11px] space-y-1" data-testid="alloc-skill-read">
            <div><Pill tone="good">READ</Pill> Real edge in Technology, Health care and Financials; Consumer discretionary picks have destroyed value.</div>
            <div>Positioning is forward-looking: {Math.round((g.lead_exp + g.follow_exp) * 100)}% of the book sits in names skilled specialists are converging on, and {Math.round(g.lead_exp * 100)} points of it were owned <b>before</b> the convergence (leader, not follower).</div>
          </div>
        </CardContent></Card>
      </div>
      <div className="text-[10px] text-muted-foreground">Real: grade, peer z, leader/follower exposure ({OBS}). Sample: return attribution, sector hit rates, quarterly selection. {GRADE_EVIDENCE}</div>
    </div>
  );
}
